import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';

const pipeMaterials = [
  { value: 'steel', label: 'Steel', roughness: 0.000045 },
  { value: 'copper', label: 'Copper', roughness: 0.0000015 },
  { value: 'aluminum', label: 'Aluminium', roughness: 0.0000015 },
  { value: 'plastic', label: 'Plastic', roughness: 0.0000015 },
];

function frictionFactor(roughness, diameterM, reynolds) {
  let f = 0.02;
  for (let i = 0; i < 100; i += 1) {
    const next =
      1 /
      (-2 * Math.log10(roughness / (3.71 * diameterM) + 2.51 / (reynolds * Math.sqrt(f)))) ** 2;
    if (Math.abs(next - f) < 1e-6) return next;
    f = next;
  }
  return f;
}

function PressureDrop() {
  const [pipeMaterial, setPipeMaterial] = useState('steel');
  const [pipeLength, setPipeLength] = useState('');
  const [pipeDiameter, setPipeDiameter] = useState('');
  const [flowRate, setFlowRate] = useState('');
  const [pressure, setPressure] = useState('');
  const [temperature, setTemperature] = useState('');

  const { result, error } = useMemo(() => {
    const length = parseNumber(pipeLength);
    const diameterMm = parseNumber(pipeDiameter);
    const flow = parseNumber(flowRate);
    const pressureNum = parseNumber(pressure);
    const temperatureNum = parseNumber(temperature);

    if (length == null || diameterMm == null || flow == null || pressureNum == null || temperatureNum == null) {
      return { result: null, error: '' };
    }
    if (diameterMm <= 0 || length <= 0) {
      return { result: null, error: 'Length and diameter must be greater than zero' };
    }

    const roughness = pipeMaterials.find((item) => item.value === pipeMaterial)?.roughness ?? 0.000045;
    const diameterM = diameterMm / 1000;
    const gasConstant = 287.05;
    const kelvin = temperatureNum + 273.15;
    const pascals = pressureNum * 1e5;
    const density = pascals / (gasConstant * kelvin);
    const area = Math.PI * (diameterM / 2) ** 2;
    const velocity = flow / 60000 / area;
    const viscosity = 1.789e-5;
    const reynolds = (density * velocity * diameterM) / viscosity;
    const f = frictionFactor(roughness, diameterM, reynolds);
    const dropPa = f * (length / diameterM) * ((density * velocity ** 2) / 2);

    return { result: dropPa / 1e5, error: '' };
  }, [pipeMaterial, pipeLength, pipeDiameter, flowRate, pressure, temperature]);

  return (
    <ToolLayout title="Pressure Drop">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Estimate straight-pipe pressure drop in a compressed-air line using Darcy-Weisbach.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl>
                <InputLabel>Pipe material</InputLabel>
                <Select
                  value={pipeMaterial}
                  label="Pipe material"
                  onChange={(event) => setPipeMaterial(event.target.value)}
                >
                  {pipeMaterials.map((material) => (
                    <MenuItem key={material.value} value={material.value}>
                      {material.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Pipe length (m)" value={pipeLength} onChange={setPipeLength} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Pipe diameter (mm)" value={pipeDiameter} onChange={setPipeDiameter} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Flow rate (L/min)" value={flowRate} onChange={setFlowRate} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Pressure (bar)" value={pressure} onChange={setPressure} min={0} />
            </Grid>
            <Grid item xs={12}>
              <NumberField label="Temperature (°C)" value={temperature} onChange={setTemperature} min={-273.15} />
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result != null && (
            <ResultPanel items={[{ label: 'Pressure drop', value: `${formatNumber(result, 4)} bar`, fullWidth: true }]} />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        ΔP = f × (L / D) × (ρ v² / 2). Flow is converted from L/min to m³/s. Fittings and valves are not included.
      </Typography>
    </ToolLayout>
  );
}

export default PressureDrop;
